'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Problem {
  id: number;
  title: string;
  description: string;
  icon: string;
  isActive: boolean;
}

export default function ProblemIdentification() {
  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedProblem, setSelectedProblem] = useState<number | null>(null);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const problems: Problem[] = [
    {
      id: 1,
      title: "Hypotéka a bydlení",
      description: "Nevíte, zda si můžete dovolit vlastní bydlení? Zmateni různými nabídkami hypoték a nejste si jisti, která je pro vás ta pravá?",
      icon: "HomeIcon",
      isActive: false
    },
    {
      id: 2,
      title: "Životní změny",
      description: "Čekáte rodinu, měníte zaměstnání nebo plánujete důchod? Velké životní změny přinášejí finanční nejistotu a potřebu nového plánu.",
      icon: "SparklesIcon",
      isActive: false
    },
    {
      id: 3,
      title: "Finanční chaos",
      description: "Máte pocit, že vaše finance jsou nepřehledné? Nevíte, kam peníze mizí a jak efektivně spořit pro budoucnost?",
      icon: "ExclamationTriangleIcon",
      isActive: false
    },
    {
      id: 4,
      title: "Investice a spoření",
      description: "Chcete začít investovat, ale nevíte jak? Bojíte se udělat špatné rozhodnutí a ztratit své úspory?",
      icon: "BanknotesIcon",
      isActive: false
    }
  ];

  const handleProblemClick = (id: number) => {
    if (!isHydrated) return;
    setSelectedProblem(selectedProblem === id ? null : id);
  };

  return (
    <section className="py-16 lg:py-24 bg-background">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto text-center mb-12">
          <h2 className="text-3xl lg:text-5xl font-bold text-text-primary mb-6">
            Poznáváte se v některé z těchto situací?
          </h2>
          <p className="text-lg text-text-secondary">
            Nejste sami. Pomáháme lidem v Praze najít cestu z finančního chaosu k jasnému plánu.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
          {problems.map((problem) => (
            <div
              key={problem.id}
              onClick={() => handleProblemClick(problem.id)}
              className={`bg-card border-2 rounded-xl p-6 cursor-pointer transition-smooth hover:shadow-elevated ${
                selectedProblem === problem.id
                  ? 'border-primary shadow-elevated'
                  : 'border-border hover:border-primary/50'
              }`}
            >
              <div className="flex items-start space-x-4">
                <div className={`flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center ${
                  selectedProblem === problem.id ? 'bg-primary' : 'bg-surface'
                }`}>
                  <Icon
                    name={problem.icon as any}
                    size={24}
                    className={selectedProblem === problem.id ? 'text-white' : 'text-primary'}
                    variant={selectedProblem === problem.id ? 'solid' : 'outline'}
                  />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-text-primary mb-2">
                    {problem.title}
                  </h3>
                  <p className="text-text-secondary leading-relaxed">
                    {problem.description}
                  </p>
                </div>
                <Icon
                  name="ChevronRightIcon"
                  size={20}
                  className={`flex-shrink-0 text-text-secondary transition-transform ${
                    selectedProblem === problem.id ? 'rotate-90' : ''
                  }`}
                />
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-12">
          <p className="text-lg text-text-primary mb-6">
            Každá situace je jedinečná. Pojďme společně najít vaše řešení.
          </p>
        </div>
      </div>
    </section>
  );
}